import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import type { CommunityBadge as CommunityBadgeRecord } from '~/interfaces/community';
import type { CommunityRole } from '~/interfaces/community/role';

import { Badge } from './Badge';
import { CommunityBadge } from './CommunityBadge';
import { RoleBadge } from './RoleBadge';
import { findHighestRole } from './roleBadges';

type StatusBadgeConfig = { labelKey: string; className: string };

const STATUS_BADGES = {
  banned: { labelKey: 'community.banned_badge', className: 'bg-red-100 text-red-600' },
  blocked: { labelKey: 'community.blocked_badge', className: 'bg-black/5 text-black/60' },
  blocked_you: { labelKey: 'community.blocked_you_badge', className: 'bg-black/5 text-black/60' },
} satisfies Record<string, StatusBadgeConfig>;

const INLINE_LIMIT = 2;

type MemberBadgeProps = {
  roles: CommunityRole[];
  isBanned?: boolean;
  isBlocked?: boolean;
  blockedYou?: boolean;
  badges?: CommunityBadgeRecord[];
  limit?: number | null;
  interactive?: boolean;
};

type Pill = {
  key: string;
  node: ReactNode;
};

const renderStatusBadge = (t: (key: string) => string, config: StatusBadgeConfig) => (
  <Badge label={t(config.labelKey)} className={`uppercase tracking-wide ${config.className}`} />
);

export const MemberBadge: FC<MemberBadgeProps> = ({
  roles,
  isBanned = false,
  isBlocked = false,
  blockedYou = false,
  badges = [],
  limit,
  interactive = true,
}) => {
  const { t } = useTranslation();

  if (isBanned) return renderStatusBadge(t, STATUS_BADGES.banned);
  if (isBlocked) return renderStatusBadge(t, STATUS_BADGES.blocked);
  if (blockedYou) return renderStatusBadge(t, STATUS_BADGES.blocked_you);

  const communityRole = findHighestRole(roles);
  const earnedPills: Pill[] = badges.map((badge) => ({
    key: badge.id,
    node: <CommunityBadge badge={badge} interactive={interactive} />,
  }));
  const pills: Pill[] = communityRole
    ? [
        { key: communityRole, node: <RoleBadge communityRole={communityRole} interactive={interactive} /> },
        ...earnedPills,
      ]
    : earnedPills;

  if (pills.length === 0) return null;

  const visible = limit === null ? pills : pills.slice(0, limit ?? INLINE_LIMIT);

  return (
    <div className="flex items-center gap-[6px] flex-wrap">
      {visible.map((pill, index) => (
        <span
          key={pill.key}
          className={cn('inline-flex items-center', limit === undefined && index > 0 && 'max-sm:hidden')}
        >
          {pill.node}
        </span>
      ))}
    </div>
  );
};
